import {
  type CreateCustomerRequest,
  type CustomerAddressInput,
  type CustomerContactInput,
  CustomerDetailSchema,
  type CustomerDetail,
  type CustomerListQuery,
  type CustomerListResponse,
  CustomerListResponseSchema,
  type DocumentDownloadResponse,
  DocumentDownloadResponseSchema,
  type DocumentUploadFinalizeRequest,
  type DocumentUploadInitResponse,
  DocumentUploadInitResponseSchema,
  CustomerDocumentKindSchema,
  type CustomerDocumentKind,
  type UpdateCustomerRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Customers CRM API surface. Every JSON response is validated with the
 * matching shared Zod schema so a backend change can't silently corrupt the
 * UI's type assumptions.
 */

// ---------------------------------------------------------------------------
// Branch options (used by the Profile step picker)
// ---------------------------------------------------------------------------

export interface BranchOption {
  id: string;
  name: string;
}

export const listBranchOptions = async (): Promise<BranchOption[]> => {
  const { data } = await apiClient.get<unknown>('/branches', {
    params: { pageSize: 200, sortBy: 'name', sortDir: 'asc' },
  });
  // The branches list endpoint returns a paginated envelope; we pull `.data`.
  const raw = data as { data?: Array<{ id: string; name: string }> };
  if (!raw?.data || !Array.isArray(raw.data)) return [];
  return raw.data.map((b) => ({ id: b.id, name: b.name }));
};

// ---------------------------------------------------------------------------
// Customer CRUD
// ---------------------------------------------------------------------------

export const listCustomers = async (query: CustomerListQuery): Promise<CustomerListResponse> => {
  const { data } = await apiClient.get<unknown>('/customers', { params: query });
  return CustomerListResponseSchema.parse(data);
};

export const getCustomer = async (id: string): Promise<CustomerDetail> => {
  const { data } = await apiClient.get<unknown>(`/customers/${id}`);
  return CustomerDetailSchema.parse(data);
};

export const createCustomer = async (payload: CreateCustomerRequest): Promise<CustomerDetail> => {
  const { data } = await apiClient.post<unknown>('/customers', payload);
  return CustomerDetailSchema.parse(data);
};

export const updateCustomer = async (
  id: string,
  payload: UpdateCustomerRequest,
): Promise<CustomerDetail> => {
  const { data } = await apiClient.patch<unknown>(`/customers/${id}`, payload);
  return CustomerDetailSchema.parse(data);
};

export const deleteCustomer = async (id: string): Promise<void> => {
  await apiClient.delete(`/customers/${id}`);
};

/** Bulk activate/deactivate customers. */
export const bulkSetCustomersStatus = async (
  ids: string[],
  isActive: boolean,
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/customers/bulk-set-status', { ids, isActive });
  return data;
};

// ---------------------------------------------------------------------------
// Addresses
// ---------------------------------------------------------------------------

export const addAddress = async (
  customerId: string,
  payload: CustomerAddressInput,
): Promise<CustomerDetail> => {
  const { data } = await apiClient.post<unknown>(`/customers/${customerId}/addresses`, payload);
  return CustomerDetailSchema.parse(data);
};

export const removeAddress = async (
  customerId: string,
  addressId: string,
): Promise<CustomerDetail> => {
  const { data } = await apiClient.delete<unknown>(
    `/customers/${customerId}/addresses/${addressId}`,
  );
  return CustomerDetailSchema.parse(data);
};

// ---------------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------------

export const addContact = async (
  customerId: string,
  payload: CustomerContactInput,
): Promise<CustomerDetail> => {
  const { data } = await apiClient.post<unknown>(`/customers/${customerId}/contacts`, payload);
  return CustomerDetailSchema.parse(data);
};

export const removeContact = async (
  customerId: string,
  contactId: string,
): Promise<CustomerDetail> => {
  const { data } = await apiClient.delete<unknown>(
    `/customers/${customerId}/contacts/${contactId}`,
  );
  return CustomerDetailSchema.parse(data);
};

// ---------------------------------------------------------------------------
// Documents - two-step S3 upload (ADR-0014)
// ---------------------------------------------------------------------------

export interface DocumentUploadMeta {
  documentKind: CustomerDocumentKind;
  title: string;
  isPrimary: boolean;
  documentNumber?: string | null;
  issuedCountryId?: string | null;
  issueDate?: string | null;
  expiryDate?: string | null;
}

/**
 * Three-stage document upload:
 *   1. POST upload-init  - server issues a presigned PUT URL and an opaque token.
 *   2. PUT presignedUrl  - raw S3 PUT with the file bytes (bypasses the API server).
 *   3. POST upload-finalize - server commits the customer_documents row.
 *
 * The raw PUT in step 2 uses the native fetch API, not apiClient, because
 * S3 presigned URLs carry the full auth in the query string and don't accept
 * extra Authorization headers.
 *
 * Max file size is enforced server-side (25 MB, per DocumentUploadInitRequestSchema).
 * A 4xx from init surfaces as a thrown error with the server's message.
 */
export const uploadCustomerDocument = async (
  customerId: string,
  file: File,
  meta: DocumentUploadMeta,
): Promise<CustomerDetail> => {
  // Step 1: init
  const initPayload = {
    documentKind: meta.documentKind,
    filename: file.name,
    mimeType: file.type,
    sizeBytes: file.size,
  };
  const { data: initData } = await apiClient.post<unknown>(
    `/customers/${customerId}/documents/upload-init`,
    initPayload,
  );
  const initResponse: DocumentUploadInitResponse = DocumentUploadInitResponseSchema.parse(initData);

  // Step 2: raw S3 PUT - no Authorization header; auth is embedded in the URL.
  const s3Response = await fetch(initResponse.presignedUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!s3Response.ok) {
    throw new Error(`S3 upload failed (${s3Response.status}): ${s3Response.statusText}`);
  }

  // Step 3: finalize
  const finalizePayload: DocumentUploadFinalizeRequest = {
    uploadToken: initResponse.uploadToken,
    isPrimary: meta.isPrimary,
    title: meta.title,
    documentNumber: meta.documentNumber ?? null,
    issuedCountryId: meta.issuedCountryId ?? null,
    issueDate: meta.issueDate ?? null,
    expiryDate: meta.expiryDate ?? null,
  };
  const { data: finalizeData } = await apiClient.post<unknown>(
    `/customers/${customerId}/documents/upload-finalize`,
    finalizePayload,
  );
  // The finalize endpoint returns the full refreshed customer (like deleteDocument),
  // not the single document row, so parse it as CustomerDetail.
  return CustomerDetailSchema.parse(finalizeData);
};

export const deleteDocument = async (
  customerId: string,
  documentId: string,
): Promise<CustomerDetail> => {
  const { data } = await apiClient.delete<unknown>(
    `/customers/${customerId}/documents/${documentId}`,
  );
  return CustomerDetailSchema.parse(data);
};

export const downloadDocument = async (
  customerId: string,
  documentId: string,
): Promise<DocumentDownloadResponse> => {
  const { data } = await apiClient.get<unknown>(
    `/customers/${customerId}/documents/${documentId}/download`,
  );
  return DocumentDownloadResponseSchema.parse(data);
};

// Re-export for consumers that only need the kind enum values
export { CustomerDocumentKindSchema };
